import { useEffect, useRef } from 'react';
import type { TokenPrice, Candle, Token, TimeframeKey } from '@arc/shared';
import {
  API_URLS,
  TIMEFRAME_TO_BINANCE,
  TIMEFRAME_TO_CG_DAYS,
} from '@arc/shared';
import { useTerminalStore } from '../stores/terminal.store.js';

// ─── CoinGecko: Batch price fetch ────────────────────────────────────────────

async function fetchCoinGeckoPrices(tokens: Token[]): Promise<TokenPrice[]> {
  const cgTokens = tokens.filter(t => t.coingeckoId);
  if (cgTokens.length === 0) return [];

  const ids = cgTokens.map(t => t.coingeckoId).join(',');
  const url = `${API_URLS.coingecko.base}/simple/price?ids=${ids}&vs_currencies=usd&include_24hr_change=true&include_24hr_vol=true&include_market_cap=true`;

  const res  = await fetch(url);
  if (!res.ok) throw new Error(`CoinGecko ${res.status}`);
  const data = await res.json() as Record<string, {
    usd: number;
    usd_24h_change: number;
    usd_24h_vol: number;
    usd_market_cap?: number;
  }>;

  const now = Date.now();
  // Skip ids CoinGecko returned nothing for: a 0 price would overwrite a
  // good live price for that symbol in the store.
  return cgTokens.filter(token => data[token.coingeckoId!]?.usd !== undefined).map(token => {
    const raw = data[token.coingeckoId!];
    const base = {
      symbol:    token.symbol,
      price:     raw?.usd ?? 0,
      priceUsd:  raw?.usd ?? 0,
      change24h: raw?.usd_24h_change ?? 0,
      volume24h: raw?.usd_24h_vol ?? 0,
      timestamp: now,
    };
    // Only include marketCap if defined (exactOptionalPropertyTypes)
    return raw?.usd_market_cap !== undefined
      ? { ...base, marketCap: raw.usd_market_cap }
      : base;
  });
}

// ─── Binance: OHLCV Klines ────────────────────────────────────────────────────

export async function fetchBinanceCandles(
  symbol: string,
  timeframe: TimeframeKey,
  limit = 500
): Promise<Candle[]> {
  const pair     = symbol + 'USDT';
  const interval = TIMEFRAME_TO_BINANCE[timeframe];
  const url      = `${API_URLS.binance.rest}/klines?symbol=${pair}&interval=${interval}&limit=${limit}`;

  const res  = await fetch(url);
  if (!res.ok) throw new Error(`Binance klines ${res.status}`);
  const raw  = await res.json() as number[][];

  return raw.map(k => ({
    time:   Math.floor((k[0] ?? 0) / 1000), // ms → seconds
    open:   parseFloat(String(k[1] ?? 0)),
    high:   parseFloat(String(k[2] ?? 0)),
    low:    parseFloat(String(k[3] ?? 0)),
    close:  parseFloat(String(k[4] ?? 0)),
    volume: parseFloat(String(k[5] ?? 0)),
  }));
}

// ─── CoinGecko: OHLC fallback (lower resolution, no API key needed) ──────────

export async function fetchCoinGeckoCandles(
  coingeckoId: string,
  timeframe: TimeframeKey
): Promise<Candle[]> {
  const days = TIMEFRAME_TO_CG_DAYS[timeframe];
  const url  = `${API_URLS.coingecko.base}/coins/${coingeckoId}/ohlc?vs_currency=usd&days=${days}`;

  const res  = await fetch(url);
  if (!res.ok) throw new Error(`CoinGecko OHLC ${res.status}`);
  const raw  = await res.json() as number[][];

  return raw.map(k => ({
    time:   Math.floor((k[0] ?? 0) / 1000),
    open:   k[1] ?? 0,
    high:   k[2] ?? 0,
    low:    k[3] ?? 0,
    close:  k[4] ?? 0,
    volume: 0, // CoinGecko free OHLC has no volume
  }));
}

// ─── Birdeye: Solana token candles ────────────────────────────────────────────

export async function fetchBirdeyeCandles(
  address: string,
  timeframe: TimeframeKey,
  limit = 500
): Promise<Candle[]> {
  const tfMap: Record<TimeframeKey, string> = {
    '1m':  '1m', '5m': '5m', '15m': '15m', '30m': '30m',
    '1h':  '1H', '4h': '4H', '1D':  '1D',  '1W':  '1W',
  };

  const url = `${API_URLS.birdeye.base}/defi/ohlcv?address=${address}&type=${tfMap[timeframe]}&limit=${limit}`;
  const apiKey = import.meta.env.VITE_BIRDEYE_API_KEY ?? '';

  const res = await fetch(url, {
    headers: { 'X-API-KEY': apiKey },
  });
  if (!res.ok) throw new Error(`Birdeye ${res.status}`);

  const json = await res.json() as {
    data?: { items?: Array<{ unixTime: number; o: number; h: number; l: number; c: number; v: number }> };
  };

  return (json.data?.items ?? []).map(item => ({
    time:   item.unixTime,
    open:   item.o,
    high:   item.h,
    low:    item.l,
    close:  item.c,
    volume: item.v,
  }));
}

// ─── API proxy: contract candles (Birdeye → GeckoTerminal, server-side) ───────
// Called for any token that has a contract address but no Binance/CoinGecko data.
// Proxying through the API avoids CORS issues with GeckoTerminal.

export async function fetchContractCandles(
  address:   string,
  chain:     string,
  timeframe: TimeframeKey,
): Promise<Candle[]> {
  const url = `/api/candles/contract?address=${encodeURIComponent(address)}&chain=${encodeURIComponent(chain)}&timeframe=${encodeURIComponent(timeframe)}`;
  const res = await fetch(url, { signal: AbortSignal.timeout(15_000) });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: res.statusText })) as { error?: string };
    throw new Error(err.error ?? `candles/contract ${res.status}`);
  }
  const data = await res.json() as { candles?: Candle[] };
  return data.candles ?? [];
}

// ─── Smart Candle Fetcher (auto-routes by token type) ────────────────────────

export async function fetchCandles(
  token: Token,
  timeframe: TimeframeKey
): Promise<Candle[]> {
  // DEX / contract-only tokens (have an address, no Binance listing) ──────────
  // Route through API proxy which tries Birdeye → GeckoTerminal server-side
  if (token.address && token.chain) {
    // Solana: always use contract route (not on Binance)
    if (token.chain === 'solana') {
      try {
        return await fetchContractCandles(token.address, 'solana', timeframe);
      } catch {
        // Fall through to CoinGecko if proxy fails
      }
    }

    // EVM DEX tokens: no coingeckoId means not on Binance/CG — use contract route
    if (token.chain !== 'solana' && !token.coingeckoId) {
      try {
        return await fetchContractCandles(token.address, token.chain, timeframe);
      } catch {
        // Fall through
      }
    }
  }

  // Major CEX pairs → Binance (most accurate, real volume). Tokens with no
  // contract address are listed by symbol (BTC, ETH, BNB on 'bsc', CoinGecko
  // search results); contract tokens only on Ethereum, so another chain's
  // token can't hit an unrelated Binance pair that shares its ticker.
  if (!token.address || !token.chain || token.chain === 'ethereum') {
    try {
      return await fetchBinanceCandles(token.symbol, timeframe);
    } catch {
      // Fall through to CoinGecko
    }
  }

  // CoinGecko OHLC (lower resolution but no key needed)
  if (token.coingeckoId) {
    try {
      return await fetchCoinGeckoCandles(token.coingeckoId, timeframe);
    } catch {
      // Fall through
    }
  }

  return [];
}

// ─── usePricePoller hook ──────────────────────────────────────────────────────

const POLL_INTERVAL_MS = 15_000; // 15s for free tier safety

// The API's Binance ticker (WS price_update, every 10s) is the live feed.
// CoinGecko lags it, so polling both made the live candle's close flick
// between two prices. CoinGecko is only the fallback for symbols the server
// hasn't priced recently: WS down, or tokens the server doesn't cover.
const SERVER_FEED_FRESH_MS = 30_000;
const serverPriceAt = new Map<string, number>();

/** Record symbols just priced by the server feed (called on WS price_update). */
export function noteServerPrices(symbols: string[], now = Date.now()): void {
  for (const s of symbols) serverPriceAt.set(s, now);
}

function hasFreshServerPrice(symbol: string, now: number): boolean {
  const at = serverPriceAt.get(symbol);
  return at !== undefined && now - at < SERVER_FEED_FRESH_MS;
}

/**
 * One CoinGecko poll for every known token. Market caps (the server feed has
 * none) are kept for all of them; prices only for tokens the server feed
 * isn't covering.
 */
export async function pollPrices(): Promise<void> {
  try {
    const store  = useTerminalStore.getState();
    const quotes = await fetchCoinGeckoPrices(store.tokens);
    const now    = Date.now();

    const caps: Record<string, number> = {};
    for (const q of quotes) if (q.marketCap !== undefined && q.marketCap > 0) caps[q.symbol] = q.marketCap;
    if (Object.keys(caps).length > 0) store.setMarketCaps(caps);

    const prices = quotes.filter(q => !hasFreshServerPrice(q.symbol, now));
    if (prices.length > 0) store.updatePrices(prices);
  } catch {
    // Network error — silently ignore, try again next tick
  }
}

export function usePricePoller() {
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const poll = () => { void pollPrices(); };
    poll(); // immediate first call
    timerRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, []);
}

// ─── useCandleLoader hook ─────────────────────────────────────────────────────

// ─── Dev-only: simulate a feed error ─────────────────────────────────────────
// Open e.g. http://localhost:4300/?debugFeedError=WIF (or =all, or =WIF,ETH).
// Client-side only, and only in dev builds. The first load of a matching
// symbol is forced to 'error' without fetching; pressing RETRY clears the
// simulation for that symbol and performs a real fetch.
const debugFeedErrorPending: Set<string> = (() => {
  if (!import.meta.env.DEV || typeof window === 'undefined') return new Set<string>();
  const raw = new URLSearchParams(window.location.search).get('debugFeedError');
  return new Set((raw ?? '').split(',').map(s => s.trim().toUpperCase()).filter(Boolean));
})();
const debugFeedErrorCleared = new Set<string>();

function consumeDebugFeedError(symbol: string, isRetry: boolean): boolean {
  if (debugFeedErrorPending.size === 0) return false;
  const sym = symbol.toUpperCase();
  if (!debugFeedErrorPending.has(sym) && !debugFeedErrorPending.has('ALL')) return false;
  if (isRetry) debugFeedErrorCleared.add(sym);
  return !debugFeedErrorCleared.has(sym);
}

export function useCandleLoader(symbol: string, timeframe: TimeframeKey) {
  const { setCandles, setFeedStatus, tokens, feedRetryNonce } = useTerminalStore();

  useEffect(() => {
    const token = tokens.find(t => t.symbol === symbol);
    if (!token) { setFeedStatus(symbol, timeframe, 'error'); return; }

    if (consumeDebugFeedError(symbol, feedRetryNonce > 0)) {
      console.info(`[debugFeedError] simulating ${symbol} ${timeframe} feed error (press RETRY to fetch)`);
      setCandles(symbol, timeframe, []);
      setFeedStatus(symbol, timeframe, 'error');
      return;
    }
    if (debugFeedErrorPending.size > 0) console.info(`[debugFeedError] fetching ${symbol} ${timeframe} candles`);

    let cancelled = false;
    setFeedStatus(symbol, timeframe, 'loading');
    fetchCandles(token, timeframe).then(candles => {
      if (cancelled) return;
      setCandles(symbol, timeframe, candles);
      setFeedStatus(symbol, timeframe, candles.length > 0 ? 'ok' : 'empty');
    }).catch(() => {
      if (!cancelled) setFeedStatus(symbol, timeframe, 'error');
    });

    return () => { cancelled = true; };
  }, [symbol, timeframe, tokens, setCandles, setFeedStatus, feedRetryNonce]);
}
